import { MessageModel } from '../message-definitions'
import { OptionSetModel } from '../option-set-definitions'
import { Sitemap, SitemapReferenceItem } from '../site-map-definitions'
import { ComponentDefinition } from './component-definition'
import { EntityModel } from './entity-model'

export class DesignModel {
  public optionSets: OptionSetModel[]
  public messages: MessageModel[]
  public sitemaps: Sitemap[]
  public entities: EntityModel[]
  public externalReferences: EntityModel[]
  public externalReferencesCache: Record<string, EntityModel[]>
  public customComponents: ComponentDefinition[]
  public sitemapItems: SitemapReferenceItem[]

  constructor() {
    this.optionSets = []
    this.messages = []
    this.sitemaps = []
    this.entities = []
    this.externalReferences = []
    this.externalReferencesCache = {}
    this.customComponents = []
    this.sitemapItems = []
  }

  public merge(model: DesignModel, msName: string | undefined) {
    const optionSets = model?.optionSets || []
    const messages = model?.messages || []
    const sitemaps = model?.sitemaps || []
    const entities = model?.entities || []
    const sitemapItems = model?.sitemapItems || []

    optionSets.forEach((opt) => {
      if (!opt.options.every((o) => o.value == 'option_to_remove') && !this.containsOptionSet(opt.name))
        this.optionSets.push(new OptionSetModel(opt))
    })

    messages.forEach((message) => {
      if (!this.containsMessage(message.code)) this.messages.push(message)
    })

    sitemaps.forEach((st) => {
      if (!this.containsSitemap(st.name)) {
        this.sitemaps.push(new Sitemap(st))
      } else {
        const siteMap = this.getSitemap(st.name) as Sitemap
        siteMap.merge(st)
      }
    })

    entities.forEach((entity) => {
      this.substituteEntity(entity.name, new EntityModel(entity))
    })

    sitemapItems.forEach((item) => {
      if (!this.containsSitemapItem(item.id)) this.sitemapItems.push(new SitemapReferenceItem(item))
      //If exists no merge because there should be only one definition with the same id
    })

    this.customComponents = this.customComponents.concat(model?.customComponents)

    if (msName) {
      this.externalReferencesCache[msName] = model?.externalReferences.map((e) => new EntityModel(e))
    } else if (model && model.externalReferencesCache) {
      Object.keys(model.externalReferencesCache).forEach((k) => {
        const entry = model.externalReferencesCache[k]
        this.externalReferencesCache[k] = entry.map((e) => new EntityModel(e))
      })
    }
  }

  public getOptionSet(optionSetName: string): OptionSetModel | undefined {
    return this.optionSets.find((opt) => opt.name == optionSetName)
  }

  public containsOptionSet(optionSetName: string): boolean {
    return !!this.getOptionSet(optionSetName)
  }

  public getMessages(messageCode: string): MessageModel | undefined {
    return this.messages.find((opt) => opt.code == messageCode)
  }

  public containsMessage(messageCode: string): boolean {
    return !!this.getMessages(messageCode)
  }

  public getSitemap(sitemapName: string): Sitemap | undefined {
    return this.sitemaps.find((st) => st.name == sitemapName)
  }

  public getSitemapItem(itemId: string): SitemapReferenceItem | undefined {
    return this.sitemapItems.find((item) => item.id === itemId)
  }

  public containsSitemap(sitemapName: string): boolean {
    return !!this.getSitemap(sitemapName)
  }

  public getEntity(entityName: string): EntityModel | undefined {
    return this.entities.find((ent) => ent.name == entityName)
  }

  public substituteEntity(entityName: string, newEntity: EntityModel): void {
    const oldModelIndex = this.entities.findIndex((ent) => ent.name == entityName)
    if (oldModelIndex >= 0) {
      this.entities.splice(oldModelIndex, 1)
    }
    this.entities.push(newEntity)
  }

  public containsEntity(entityName: string): boolean {
    return !!this.getEntity(entityName)
  }

  public containsSitemapItem(itemId: string): boolean {
    return !!this.getSitemapItem(itemId)
  }

  public getExternalEntity(entityName: string, msName: string): EntityModel | undefined {
    const entries = this.externalReferencesCache[msName] ?? []
    return entries.find((e) => e.name === entityName)
  }

  public containsExternalEntity(entityName: string, msName: string): boolean {
    const entries = this.externalReferencesCache[msName] ?? []
    return !!entries.find((e) => e.name === entityName)
  }
}
